import { createElement as rc, useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { View, Button, styled, fromTheme, webOnlyStyles } from 'lib_ui-primitives';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

const GraphFilters = styled(View).attrs({ name: 'graph-filters' })`
    align-self: flex-end;
    flex-grow: 1;
    max-width: 100%;
`;
GraphFilters.displayName = 'GraphFilters';

const InnerGraphFilters = styled(View).attrs({ name: 'inner-graph-filters' })`
    flex-direction: column;
    justify-content: flex-start;
    flex-grow: 1;
    align-items: flex-end;
`;
InnerGraphFilters.displayName = 'InnerGraphFilters';

let ActualFilters = styled(View).attrs({ name: 'actual-filters' })`
    position: fixed;
    padding: ${fromTheme('menu', 'padding')};
    display: ${({ filtersVisible }) => (filtersVisible ? 'flex' : 'none')};
    background-color: ${fromTheme('menu', 'backgroundColor')};
    border-radius: ${fromTheme('menu', 'borderRadius')};
    border: ${fromTheme('menu', 'borderWidth')} solid ${fromTheme('menu', 'borderColor')};
    overflow: visible;
    z-index: ${({ theme }) => theme.zindex.DropDownList};
    & > * {
        min-height: 120px;
    }
`;
ActualFilters.displayName = 'ActualFilters';
ActualFilters = webOnlyStyles(ActualFilters)`
    box-shadow: ${fromTheme('menu', 'boxShadow')};
`;

function useButtonPosition(buttonRef, filtersVisible) {
    const [position, setPosition] = useState({ top: 0, right: 0 });
    useEffect(() => {
        if (filtersVisible && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 8,
                right: window.innerWidth - rect.right
            });
        }
    }, [filtersVisible, buttonRef]);
    return position;
}

export default function FilterContainer(props) {
    const { children: filterControls } = props;
    const [filtersVisible, setFiltersVisible] = useState(false);
    const theme = useTheme();
    const buttonRef = useRef(null);
    const position = useButtonPosition(buttonRef, filtersVisible);

    const toggleFilter = useCallback(() => {
        setFiltersVisible(prevState => {
            return !prevState;
        });
    }, []);

    const buttonProps = useMemo(() => {
        if (theme.selectedTheme === 'gl2025') {
            return {
                icon: filtersVisible ? 'x' : 'filters-lines',
                variant: 'secondary',
                size: 'sm',
                text: 'Filter',
                onClick: toggleFilter
            };
        }
        return {
            buttonStyle: 'IconAction',
            hoverText: 'Filter options',
            alt: 'filter options',
            icon: filtersVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down',
            onClick: toggleFilter
        };
    }, [filtersVisible, theme.selectedTheme, toggleFilter]);

    if (!filterControls) return null;
    if (filterControls.length === 0) return null;

    const filtersPopup = createPortal(
        rc(ActualFilters, { filtersVisible, style: { top: position.top, right: position.right } }, filterControls),
        document.body
    );

    // prettier-ignore
    return rc(GraphFilters, null,
        rc(InnerGraphFilters, null,
            rc(Button, { ...buttonProps, ref: buttonRef }),
            filtersPopup
        )
    );
}

FilterContainer.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element])
};
